import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { isVideoFile } from "../utils/mediaUtils";
import "../styles/selectPlaces.css"

const companionshipOptions = ['Alone', 'Couple', 'Couples', 'Family', 'Friends'];
const currencyOptions = ['USD', 'EUR', 'GBP', 'JPY'];

function TrippinPlaceSelect({ isTagged = false }) {
  const { trippinId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placeDetails, setPlaceDetails] = useState({});
  const [expandedPlace, setExpandedPlace] = useState(null);
  const [backgroundPhotos, setBackgroundPhotos] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [closeFriends, setCloseFriends] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [taggingPlaceName, setTaggingPlaceName] = useState(null);  // which place is being tagged?
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    fetchPlaces();
    restoreStateFromSessionStorage();
  }, [trippinId]);

  useEffect(() => {
    if (location.state?.selectedPhotos && expandedPlace) {
      setPlaceDetails((prev) => ({
        ...prev,
        [expandedPlace]: {
          ...(prev[expandedPlace] || {}),
          selectedPhotos: location.state.selectedPhotos,
        },
      }));
    }
  }, [location.state, expandedPlace]);

  useEffect(() => {
    const fetchCloseUsers = async () => {
      try {
        const response = await axiosInstance.get('/flashes/close-users');
        setCloseFriends(response.data.users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    fetchCloseUsers();
  }, []);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      // Decide which endpoint to use
      const endpoint = isTagged
        ? `/trippins/${trippinId}/placesTagged`
        : `/trippins/${trippinId}/places`;
      const res = await axiosInstance.get(endpoint);
      const data = res.data || [];

      if (typeof data === 'object' && data.message) {
        // e.g. "You have posted everything."
        setPlaces([]);
      }
      else {
        setPlaces(data);
      }
    } catch (err) {
      console.error('Error fetching places:', err);
    } finally {
      setLoading(false);
    }
  };

  const restoreStateFromSessionStorage = () => {
    const savedPlaceDetails = JSON.parse(sessionStorage.getItem('placeDetails')) || {};
    setPlaceDetails(savedPlaceDetails);
  };

  const handleDetailChange = (placeName, field, value) => {
    setPlaceDetails((prev) => ({
      ...prev,
      [placeName]: {
        ...prev[placeName],
        [field]: value,
      },
    }));
  };

  const generateSignedUrls = async (photos) => {
    return Promise.all(
      photos.map(async (photo) => {
        const signedUrl = await axiosInstance
          .get(`/trippinPosts/generateSignedUrl`, { params: { filePath: photo } })
          .then((res) => res.data.signedUrl)
          .catch((err) => {
            console.error("Error generating signed URL:", err);
            return null;
          });
  
        return {
          original: photo,
          signedUrl,
          isVideo: isVideoFile(signedUrl), // Check if it's a video
        };
      })
    );
  };

  const handleNext = async () => {
    sessionStorage.setItem('placeDetails', JSON.stringify(placeDetails));
  
    const storedPhotos = JSON.parse(sessionStorage.getItem('selectedPhotos')) || {};
    const photos = Object.values(storedPhotos).flat();
  
    if (photos.length === 0) {
      alert('Please select at least one photo to proceed.');
      return;
    }

    const photosWithSignedUrls = await generateSignedUrls(photos);
    setBackgroundPhotos(photosWithSignedUrls);
    setIsDialogOpen(true);
  };

  const handlePost = async (isCloseFriends) => {
    setPosting(true);
    try {
      const payload = {
        trippin_id: trippinId,
        selectedPhotos: JSON.parse(sessionStorage.getItem('selectedPhotos')) || {},
        placeDetails,
        background_url: selectedBackground,
        isCloseFriends,
      };

      await axiosInstance.post('/trippinPosts', payload);
      alert("Post successful!");
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setPosting(false);
    }
  };

  const openTagDialog = (placeName) => {
    setTaggingPlaceName(placeName);

    const existingTags = placeDetails[placeName]?.tags || [];
    setSelectedUsers(existingTags);

    setIsTagDialogOpen(true);
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/flashes/search-users', {
        params: { query: term },
      });
      setFilteredUsers(response.data.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (user) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.user_id === user.user_id);
      if (exists) {
        return prev.filter((u) => u.user_id !== user.user_id);
      }
      return [...prev, user];
    });
  };

  const confirmTags = () => {
    if (!taggingPlaceName) return;
  
    setPlaceDetails((prev) => ({
      ...prev,
      [taggingPlaceName]: {
        ...(prev[taggingPlaceName] || {}),
        tags: selectedUsers.map((u) => ({ user_id: u.user_id, username: u.username })),
      },
    }));
  
    setIsTagDialogOpen(false);
    setTaggingPlaceName(null);
    setSelectedUsers([]);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: 'black' }} />
      </div>
    );
  }

  if (!loading && places.length === 0) {
    return <div>You have posted everything.</div>;
  }

  const displayedUsers = searchTerm ? filteredUsers : closeFriends;

  return (
    <div className="select-places-container">
      <h2 className="select-places-title">
        Select Places
      </h2>

      {places.map((pl) => (
        <div className="place-card" key={pl.place_name}>
          <div className="place-card-header">
            <div
              className="place-info"
              onClick={() =>
                navigate(
                  isTagged
                    ? `/taggedtrippins/${trippinId}/photoSelect/${encodeURIComponent(pl.place_name)}`
                    : `/yourtrippins/${trippinId}/photoSelect/${encodeURIComponent(pl.place_name)}`
                )
              }
            >
              <div>
                <div className="place-name">{pl.place_name}</div>
                <div className="photo-count">
                  {pl.count_content} {pl.count_content == 1 ? 'item' : 'items'}
                </div>
              </div>
            </div>
            <div
              className={`expand-toggle ${
                expandedPlace === pl.place_name ? 'expanded' : ''
              }`}
              onClick={() =>
                setExpandedPlace((prev) => (prev === pl.place_name ? null : pl.place_name))
              }
            >
              ▼
            </div>
          </div>

          {expandedPlace === pl.place_name && (
            <div className="expanded-content">
              <h4>Details for <i>{pl.place_name}</i></h4>

              <div className="form-row">
                <label className="form-label">Caption:</label>
                <input
                  type="text"
                  value={placeDetails[pl.place_name]?.caption || ''}
                  onChange={(e) => handleDetailChange(pl.place_name, 'caption', e.target.value)}
                />
              </div>

              <div className="form-row">
                <label className="form-label">Tags:</label>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#2c3e50',
                    boxShadow: 'none',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '6px',
                  }}
                  onClick={() => openTagDialog(pl.place_name)}
                >
                  Add Users
                </Button>
              </div>

              {placeDetails[pl.place_name]?.tags?.length > 0 && (
                <div className="tagged-users">
                  Tagged Users:{' '}
                  {placeDetails[pl.place_name].tags.map((tag) => tag.username).join(', ')}
                </div>
              )}
        
              <div className="form-row">
                <label className="form-label">Companionship:</label>
                <select
                  value={placeDetails[pl.place_name]?.companionship || ''}
                  onChange={(e) =>
                    handleDetailChange(pl.place_name, 'companionship', e.target.value)
                  }
                >
                  <option value="">-- Select an option --</option>
                  {companionshipOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label className="form-label">Review (1-5):</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={placeDetails[pl.place_name]?.review || ''}
                  onChange={(e) => handleDetailChange(pl.place_name, 'review', e.target.value)}
                />
              </div>

              <div className="form-row">
                <label className="form-label">Price:</label>
                <input
                  type="number"
                  min="0"
                  value={placeDetails[pl.place_name]?.price || ''}
                  onChange={(e) => handleDetailChange(pl.place_name, 'price', e.target.value)}
                />
                <select
                  value={placeDetails[pl.place_name]?.currency || 'USD'}
                  onChange={(e) => handleDetailChange(pl.place_name, 'currency', e.target.value)}
                >
                  {currencyOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Dialog for choosing background image */}
      {isDialogOpen && (
        <div
          className="custom-dialog-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains('custom-dialog-backdrop')) {
              setIsDialogOpen(false);
            }
          }}
        >
          <div className="custom-dialog-content">
            <h3>Select background for your trippin</h3>
            <div className="background-photo-grid">
              {backgroundPhotos.map((photo, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    width: '30%',
                    paddingBottom: 'calc(30% * 4 / 3)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  className={
                    (selectedBackground === photo.original ? 'background-photo-selected' : '')
                  }
                  onClick={() => setSelectedBackground(photo.original)}
                >
                  {photo.isVideo ? (
                    <video
                      src={photo.signedUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={photo.signedUrl || "/defaultPlaceholder.jpg"}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        top: 0,
                        left: 0,
                      }}
                      alt={`background-${index}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <button
                onClick={() => {
                  if (!selectedBackground) {
                    alert('Please select a background to proceed.');
                    return;
                  }
                  handlePost(false)
                }}
                className="confirm-button"
                disabled={posting}
              >
                {posting ? 'Posting...' : 'Post'}
              </button>

              <button
                onClick={() => {
                  if (!selectedBackground) {
                    alert('Please select a background to proceed.');
                    return;
                  }
                  handlePost(true)
                }}
                className="confirm-button"
                disabled={posting}
              >
                {posting ? 'Posting...' : 'Post Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog for Tagging Users */}
      <Dialog
        open={isTagDialogOpen}
        onClose={() => setIsTagDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              padding: "14px",
              maxWidth: "400px",
              width: "100%",
            },
          },
        }}
      >
        <DialogTitle>Select users to tag</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
          />
          <List sx={{ maxHeight: "250px", overflowY: "auto", mt: 1 }}>
            {displayedUsers.map((user) => (
              <ListItem
                key={user.user_id}
                button
                onClick={() => toggleUserSelection(user)}
                sx={{
                  padding: 0,
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.05)" },
                }}
              >
                <Checkbox checked={selectedUsers.some((u) => u.user_id === user.user_id)} />
                <ListItemText primary={user.username} />
              </ListItem>
            ))}

            {loadingUsers && (
              <ListItem sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-evenly" }}>
          <Button
            onClick={confirmTags}
            sx={{
              textTransform: "none",
              fontSize: "16px",
              padding: "5px 16px",
              backgroundColor: "#2c3e50",
              color: "white",
              borderRadius: "5px",
            }}
          >
            Confirm
          </Button>
          <Button
            onClick={() => setIsTagDialogOpen(false)}
            sx={{
              textTransform: "none",
              fontSize: "16px",
              padding: "5px 16px",
              backgroundColor: "#ff3040",
              color: "white",
              borderRadius: "5px",
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Next button */}
      <button onClick={handleNext} className="next-button">
        Next
      </button>
    </div>
  );
}

export default TrippinPlaceSelect;